export const CANVAS_WORLD_SELECTOR = '.uml-canvas-world';
export const THUMB_WIDTH = 640;
export const THUMB_HEIGHT = 480;
export const THUMB_PADDING = 24;
export const PREVIEW_PACKAGE_BACKGROUND = 'rgba(74, 106, 143, 0.55)'; // lighter than the live .uml-package-widget bg (rgba(28,43,63,0.35)) — that blends into the black preview backdrop otherwise

/**
 * Prepares a cloned .uml-canvas-world for capture/display OUTSIDE the live canvas
 * (html-to-image capture, or the Export Image dialog's live preview) — mutates the
 * clone in place, three concerns:
 * 1. Lightens package backgrounds (same reason as PREVIEW_PACKAGE_BACKGROUND above).
 * 2. Strips .uml-connector-anchor-layer entirely — those are edit-only drag handles
 *    (UmlConnectorAnchorHandle.jsx), not part of the UML notation, AND they're an HTML
 *    <div> inside an SVG <foreignObject> — nested foreignObject-in-foreignObject (our
 *    own capture wraps the whole clone in another one) is a construct html-to-image
 *    doesn't reliably rasterize, so the visible "start" anchor square silently
 *    disappears from captures otherwise.
 * 3. Inlines connector-label fill/stroke — normally resolved from UmlConnectorLayer.css
 *    (an external stylesheet, not an inline style). html-to-image doesn't reliably pick
 *    up externally-stylesheet-driven SVG presentation properties either (same category
 *    of gap as the font-embedding SecurityError skipFonts works around) — labels
 *    silently fall back to the SVG default (black) otherwise.
 * 4. Same gap, same fix, for CsSeqDia's message arrows (UmlSeqMessageWidget.css) — the
 *    line/arrowhead stroke/fill also comes from an external stylesheet, so sequence-
 *    diagram arrows were rendering fully invisible in captures (2026-07-19).
 * @param {HTMLElement} clone - the cloned .uml-canvas-world node
 */
export function prepareCloneForCapture(clone) {
    clone.querySelectorAll('.uml-package-widget').forEach(function lightenPackageBg(el) {
        el.style.background = PREVIEW_PACKAGE_BACKGROUND;
    });
    clone.querySelectorAll('.uml-connector-anchor-layer').forEach(function removeAnchorLayer(el) {
        el.remove();
    });
    clone.querySelectorAll('.uml-connector-label').forEach(function inlineLabelStyle(el) {
        // Dark outline-halo BEHIND the text (paint-order:stroke) so it stays readable over
        // crossing lines/shapes — same technique as the live UmlConnectorLayer.css, but fully
        // inlined here (missing paint-order last time made the stroke sit ON TOP and blob).
        el.style.fill = '#e8edf5'; // dimmed white, not grey
        el.style.stroke = '#112233';
        el.style.strokeWidth = '4px';
        el.style.paintOrder = 'stroke';
        el.style.strokeLinejoin = 'round';
    });
    clone.querySelectorAll('.uml-connector-label--derived').forEach(function inlineDerivedLabelStyle(el) {
        el.style.fill = '#c9d2e0'; // slightly dimmer than the plain label, keeps the same relative distinction
        el.style.stroke = '#112233';
        el.style.strokeWidth = '4px';
        el.style.paintOrder = 'stroke';
        el.style.strokeLinejoin = 'round';
        el.style.fontStyle = 'italic';
    });
    clone.querySelectorAll('.uml-seq-message-widget-line-solid').forEach(function inlineSeqLineSolid(el) {
        el.style.stroke = '#7fa8d9';
        el.style.strokeWidth = '1.5';
    });
    clone.querySelectorAll('.uml-seq-message-widget-line-boundary').forEach(function inlineSeqLineBoundary(el) {
        el.style.stroke = '#b98a4a';
        el.style.strokeWidth = '1.5';
        el.style.strokeDasharray = '4 3';
    });
    clone.querySelectorAll('.uml-seq-message-widget-arrowhead').forEach(function inlineSeqArrowhead(el) {
        el.style.fill = '#7fa8d9';
    });
}

/**
 * One double-rAF so a freshly-appended clone has actually painted before capture.
 * @returns {Promise<void>}
 */
export function waitForPaint() {
    return new Promise(function resolveOnNextFrame(resolve) {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
}

/**
 * Fit-to-bounds scale/translate, centered within the padded area — same idea
 * as UmlViewportEngine.zoomAt, but against a fixed thumbnail size instead of
 * the live viewport size. width/height/padding default to the schema-preview
 * constants (THUMB_*) — pass smaller values for a smaller preview area (e.g.
 * the inline package-preview in UmlPkgSaveDlg).
 * @param {{minX:number, minY:number, width:number, height:number}|null} bounds
 * @param {number} width
 * @param {number} height
 * @param {number} padding
 * @returns {string} CSS transform value
 */
export function computeFitTransform(bounds, width = THUMB_WIDTH, height = THUMB_HEIGHT, padding = THUMB_PADDING) {
    if (!bounds) return 'none';
    const availableW = width - padding * 2;
    const availableH = height - padding * 2;
    const scale = Math.min(availableW / bounds.width, availableH / bounds.height, 1);
    // Center within the available (padded) area — one axis fits exactly (the
    // limiting one), the other has leftover room; split that leftover evenly
    // instead of anchoring everything to the top-left corner.
    const extraX = availableW - bounds.width * scale;
    const extraY = availableH - bounds.height * scale;
    const panX = padding + extraX / 2 - bounds.minX * scale;
    const panY = padding + extraY / 2 - bounds.minY * scale;
    return `translate(${panX}px, ${panY}px) scale(${scale})`;
}
